import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  PlusIcon,
  MinusIcon,
  ShoppingCartIcon,
  CreditCardIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { groupOrdersApi } from '../utils/advancedOrdersApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';

const GroupOrderMealSelector = ({
  isOpen,
  onClose,
  groupOrder,
  onMealsAdded,
  onPaymentComplete
}) => {
  const [meals, setMeals] = useState([]);
  const [selectedMeals, setSelectedMeals] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState('select'); // select, confirm, pay
  const [userBalance, setUserBalance] = useState(0);

  useEffect(() => {
    if (isOpen && groupOrder?.restaurant?._id) {
      fetchMeals();
      fetchUserBalance();
    }
  }, [isOpen, groupOrder]);

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const response = await groupOrdersApi.getRestaurantMeals(groupOrder.restaurant._id);
      if (response.success) {
        // Extract meals array from the nested data structure
        setMeals(response.data?.meals || []);
      } else {
        showErrorNotification('Failed to load restaurant meals');
      }
    } catch (error) {
      console.error('Failed to fetch meals:', error);
      showErrorNotification('Failed to load restaurant meals');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      // First try to get username from token
      const storedUser = JSON.parse(localStorage.getItem('byteUser') || '{}');
      const username = storedUser.username;
      
      if (!username) {
        console.error('No username found for balance fetch');
        return;
      }
      
      const response = await fetch(`https://mongobyte-hc73.onrender.com/api/v1/users/balance/${username}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserBalance(data.balance || 0);
      } else {
        console.error('Failed to fetch balance:', response.status);
      }
    } catch (error) {
      console.error('Failed to fetch user balance:', error);
    }
  };

  const updateMealQuantity = (mealId, change) => {
    setSelectedMeals(prev => {
      const newMap = new Map(prev);
      const currentQuantity = newMap.get(mealId) || 0;
      const newQuantity = Math.max(0, currentQuantity + change);

      if (newQuantity === 0) {
        newMap.delete(mealId);
      } else {
        newMap.set(mealId, newQuantity);
      }

      return newMap;
    });
  };

  const calculateTotal = () => {
    let total = 0;
    selectedMeals.forEach((quantity, mealId) => {
      const meal = meals.find(m => m._id === mealId);
      if (meal) {
        total += meal.price * quantity;
      }
    });
    return total;
  };

  const getSelectedMealsArray = () => {
    const result = [];
    selectedMeals.forEach((quantity, mealId) => {
      const meal = meals.find(m => m._id === mealId);
      if (meal) {
        result.push({ meal, quantity });
      }
    });
    return result;
  };

  const handleAddMeals = async () => {
    if (selectedMeals.size === 0) {
      showErrorNotification('Please select at least one meal');
      return;
    }

    setSubmitting(true);
    try {
      const mealsToAdd = Array.from(selectedMeals.entries()).map(([mealId, quantity]) => ({
        meal: mealId,
        quantity
      }));

      const response = await groupOrdersApi.addMealsToGroupOrder(groupOrder._id, mealsToAdd);

      if (response.success) {
        showSuccessNotification('Meals added to group order successfully!');
        setCurrentStep('confirm');
        onMealsAdded();
      } else {
        showErrorNotification(response.message || 'Failed to add meals to group order');
      }
    } catch (error) {
      console.error('Failed to add meals:', error);
      showErrorNotification('Failed to add meals to group order');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    const total = calculateTotal();

    // Validate that meals are selected
    if (selectedMeals.size === 0) {
      showErrorNotification('Please select at least one meal before payment');
      return;
    }

    if (total <= 0) {
      showErrorNotification('Cannot process payment for an empty order');
      return;
    }

    if (userBalance < total) {
      showErrorNotification(`Insufficient balance. You need ₦${total - userBalance} more.`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await groupOrdersApi.payForGroupOrder(groupOrder._id);

      if (response.success) {
        showSuccessNotification('Payment processed successfully, delivery fee was deducted!');
        setCurrentStep('pay');
        onPaymentComplete();
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        showErrorNotification(response.message || 'Payment failed');
      }
    } catch (error) {
      console.error('Payment failed:', error);
      showErrorNotification('Payment failed');
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setSelectedMeals(new Map());
    setCurrentStep('select');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
        onClick={resetAndClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-accentwhite rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-secondary-200">
            <div>
              <h2 className="text-2xl font-bold text-crust font-display">
                {currentStep === 'select' && 'Select Your Meals'}
                {currentStep === 'confirm' && 'Confirm Your Order'}
                {currentStep === 'pay' && 'Payment Complete'}
              </h2>
              <p className="text-sm text-olive-light font-sans">
                {groupOrder?.restaurant?.name} • {groupOrder?.title}
              </p>
            </div>
            <button
              onClick={resetAndClose}
              className="text-olive-light hover:text-pepperoni p-2 rounded-xl hover:bg-secondary-50 transition-all duration-200"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center p-4 bg-secondary-50">
            <div className="flex items-center space-x-4">
              <div className={`flex items-center gap-2 ${currentStep === 'select' ? 'text-pepperoni' : 'text-green-600'
                }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'select' ? 'bg-pepperoni text-white' : 'bg-green-600 text-white'
                  }`}>
                  {currentStep === 'select' ? '1' : <CheckCircleIcon className="w-5 h-5" />}
                </div>
                <span className="font-bold font-display">Select Meals</span>
              </div>

              <div className="w-8 h-0.5 bg-secondary-300"></div>

              <div className={`flex items-center gap-2 ${currentStep === 'confirm' ? 'text-pepperoni' :
                  currentStep === 'pay' ? 'text-green-600' : 'text-secondary-400'
                }`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${currentStep === 'confirm' ? 'bg-pepperoni text-white' :
                    currentStep === 'pay' ? 'bg-green-600 text-white' : 'bg-secondary-300 text-secondary-600'
                  }`}>
                  {currentStep === 'pay' ? <CheckCircleIcon className="w-5 h-5" /> : '2'}
                </div>
                <span className="font-bold font-display">Payment</span>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[60vh] overflow-y-auto">
            {currentStep === 'select' && (
              <div className="space-y-4">
                {/* Instructions */}
                <div className="bg-blue-50 rounded-xl p-4 border-2 border-blue-200">
                  <h4 className="font-bold text-blue-800 font-display mb-2">How Group Ordering Works</h4>
                  <div className="text-sm text-blue-700 font-sans space-y-1">
                    <p>• Select the meals you want from {groupOrder?.restaurant?.name}</p>
                    <p>• Add them to the group order</p>
                    <p>• Pay for your portion when ready</p>
                    <p>• The order will be placed when everyone has paid</p>
                  </div>
                </div>

                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni"></div>
                    <span className="ml-3 text-olive-light font-sans">Loading meals...</span>
                  </div>
                ) : meals.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingCartIcon className="w-16 h-16 text-secondary-300 mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-crust font-display mb-2">
                      No meals available
                    </h3>
                    <p className="text-olive-light font-sans">
                      This restaurant doesn't have any meals available right now.
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {meals.map((meal) => {
                      const quantity = selectedMeals.get(meal._id) || 0;

                      return (
                        <motion.div
                          key={meal._id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`bg-gray-50 rounded-xl p-4 border-2 transition-all duration-300 ${quantity > 0 ? 'border-pepperoni bg-primary-50' : 'border-transparent hover:border-secondary-300'
                            }`}
                        >
                          <div className="flex items-start gap-4">
                            {meal.imageUrl && (
                              <img
                                src={meal.imageUrl}
                                alt={meal.name}
                                className="w-16 h-16 object-cover rounded-lg"
                              />
                            )}
                            <div className="flex-1">
                              <h4 className="font-bold text-crust font-display mb-1">
                                {meal.name}
                              </h4>
                              <p className="text-sm text-olive-light font-sans mb-2 line-clamp-2">
                                {meal.description}
                              </p>
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-pepperoni font-display">
                                  ₦{meal.price}
                                </span>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => updateMealQuantity(meal._id, -1)}
                                    disabled={quantity === 0}
                                    className="w-8 h-8 rounded-full bg-secondary-200 hover:bg-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
                                  >
                                    <MinusIcon className="w-4 h-4 text-olive-light" />
                                  </button>
                                  <span className="w-8 text-center font-bold text-crust font-display">
                                    {quantity}
                                  </span>
                                  <button
                                    onClick={() => updateMealQuantity(meal._id, 1)}
                                    className="w-8 h-8 rounded-full bg-pepperoni hover:bg-primary-800 text-white flex items-center justify-center transition-all duration-200"
                                  >
                                    <PlusIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {currentStep === 'confirm' && (
              <div className="space-y-6">
                <div className="bg-secondary-50 rounded-xl p-6">
                  <h3 className="text-lg font-bold text-crust font-display mb-4">
                    Order Summary
                  </h3>
                  <div className="space-y-3">
                    {getSelectedMealsArray().map(({ meal, quantity }) => (
                      <div key={meal._id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {meal.imageUrl && (
                            <img
                              src={meal.imageUrl}
                              alt={meal.name}
                              className="w-12 h-12 object-cover rounded-lg"
                            />
                          )}
                          <div>
                            <div className="font-bold text-crust font-display">
                              {meal.name}
                            </div>
                            <div className="text-sm text-olive-light font-sans">
                              ₦{meal.price} × {quantity}
                            </div>
                          </div>
                        </div>
                        <div className="font-bold text-pepperoni font-display">
                          ₦{meal.price * quantity}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-secondary-200 mt-4 pt-4">
                    <div className="flex items-center justify-between text-lg">
                      <span className="font-bold text-crust font-display">Total:</span>
                      <span className="font-bold text-pepperoni font-display">
                        ₦{calculateTotal()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl p-4 border-2 border-yellow-200">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">₦</span>
                    </div>
                    <div>
                      <div className="font-bold text-crust font-display">
                        Your Balance: ₦{userBalance}
                      </div>
                      <div className="text-sm text-olive-light font-sans">
                        {userBalance >= calculateTotal() ?
                          'Sufficient balance for payment' :
                          `Need ₦${calculateTotal() - userBalance} more`
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {currentStep === 'pay' && (
              <div className="text-center py-12">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"
                >
                  <CheckCircleIcon className="w-12 h-12 text-green-600" />
                </motion.div>
                <h3 className="text-2xl font-bold text-crust font-display mb-2">
                  Payment Successful!
                </h3>
                <p className="text-olive-light font-sans mb-4">
                  Your meals have been added to the group order and payment has been processed.
                </p>
                <div className="bg-green-50 rounded-xl p-4 border-2 border-green-200">
                  <div className="text-sm text-green-800 font-sans">
                    Amount Paid: <span className="font-bold">₦{calculateTotal()}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          {currentStep !== 'pay' && (
            <div className="flex gap-4 p-6 border-t border-secondary-200">
              <button
                onClick={resetAndClose}
                disabled={submitting}
                className="flex-1 py-3 px-6 border-2 border-secondary-300 text-olive-light rounded-xl font-bold font-display hover:bg-secondary-50 transition-all duration-300 disabled:opacity-50"
              >
                Cancel
              </button>

              {currentStep === 'select' && (
                <button
                  onClick={handleAddMeals}
                  disabled={submitting || selectedMeals.size === 0}
                  className="flex-1 py-3 px-6 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-accentwhite"></div>
                      <span>Adding...</span>
                    </>
                  ) : (
                    <>
                      <ShoppingCartIcon className="w-5 h-5" />
                      <span>Add to Group Order (₦{calculateTotal()})</span>
                    </>
                  )}
                </button>
              )}

              {currentStep === 'confirm' && (
                <button
                  onClick={handlePayment}
                  disabled={submitting || userBalance < calculateTotal()}
                  className="flex-1 py-3 px-6 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-600 text-white rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submitting ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <CreditCardIcon className="w-5 h-5" />
                      <span>Pay Now (₦{calculateTotal()})</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GroupOrderMealSelector;